import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Mail, Linkedin, MapPin } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import AuthorAvatar from '@/components/AuthorAvatar';
import SiteFooter from '@/components/SiteFooter';
import { AUTHOR, ORG, personLd } from '@/config/authorProfile';

// Central author registry. When we add more authors, extend this object.
const AUTHORS = {
  [AUTHOR.slug]: AUTHOR,
};

export default function AuthorPage() {
  const { slug } = useParams();
  const author = AUTHORS[slug];
  if (!author) return <Navigate to="/masthead" replace />;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personLd()) }}
      />

      <header className="container mx-auto px-4 sm:px-6 py-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <AppLogo size={36} />
            <p className="heading text-sm font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </p>
          </Link>
          <Link to="/masthead" className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">All authors</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16 max-w-3xl">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-6 pb-8"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-4">
            Author
          </p>
          <div className="flex items-center gap-5 pb-6 border-b border-slate-200">
            <AuthorAvatar size={80} />
            <div className="min-w-0">
              <h1 className="heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                {author.name}
              </h1>
              <p className="text-slate-600 mt-1">{author.role} · {ORG.name}</p>
              <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {author.location}
              </p>
            </div>
          </div>
        </motion.section>

        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-8">
            <div className="prose prose-slate max-w-none text-[15px] leading-[1.75] text-slate-700 space-y-4">
              {author.bio.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-7">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3">Background</h2>
            <ul className="space-y-2 text-sm text-slate-700">
              {author.credentials.map((c, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="p-6 sm:p-7">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3">Contact</h2>
            <div className="flex flex-wrap gap-3 text-sm">
              <a
                href={`mailto:${author.email}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-md transition-colors"
                data-testid="author-contact-email"
              >
                <Mail className="w-4 h-4" /> {author.email}
              </a>
              {author.linkedin && (
                <a
                  href={author.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 hover:border-slate-500 rounded-md transition-colors"
                >
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-4">
              Replies {author.responseTimePromise}. For corrections, use "Correction"
              in the subject — see the{' '}
              <Link to="/corrections" className="text-blue-600 hover:underline">corrections page</Link>.
            </p>
          </CardContent>
        </Card>
      </main>

      <SiteFooter />
    </div>
  );
}
